/**
 * Resource Watchdog status panel — cross-process host guardian for the planning VM.
 *
 * Polls GET /api/resource-watchdog/status every 30s. Shows cgroup memory pressure,
 * kill count, and service health at a glance. The watchdog kills non-allowlisted
 * agent-spawned processes that exceed per-resource thresholds (RSS/CPU/swap).
 *
 * Display logic lives in pure, exported mappers (vitest-covered in
 * ResourceWatchdog.test.ts) — the React shell only owns the fetch + layout.
 */

import { useEffect, useState } from "react";

import { Panel } from "./components";
import type { ResourceWatchdogStatus } from "./types";

// ── pure display mappers (vitest-covered) ──────────────────────────────────

const _RED = "var(--status-stale)";
const _AMBER = "var(--status-blocked)";
const _GREEN = "var(--status-working)";
const _MUTE = "var(--fg-2)";

const POLL_INTERVAL_MS = 30_000;

export function pressureTone(level: string): string {
  if (level === "high") return _RED;
  if (level === "normal") return _GREEN;
  return _MUTE; // unknown
}

export function serviceTone(active: boolean, fresh: boolean): string {
  if (!active) return _RED;
  if (!fresh) return _AMBER;
  return _GREEN;
}

export function killCountTone(count: number): string {
  if (count > 10) return _RED;
  if (count > 0) return _AMBER;
  return _MUTE;
}

export function formatUptime(seconds: number): string {
  if (seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── panel ──────────────────────────────────────────────────────────────────

interface ResourceWatchdogProps {
  baseUrl: string;
}

export function ResourceWatchdog({ baseUrl }: ResourceWatchdogProps) {
  const [data, setData] = useState<ResourceWatchdogStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const resp = await fetch(`${baseUrl}/api/resource-watchdog/status`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = (await resp.json()) as ResourceWatchdogStatus;
        if (!cancelled) {
          setData(json);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "fetch failed");
        }
      }
    }

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [baseUrl]);

  const active = data?.service_active ?? false;
  const fresh = data?.status_file_fresh ?? false;
  const pressure = data?.pressure_level ?? "unknown";
  const cgroupMem = data?.cgroup_mem_gb ?? 0;
  const cgroupMax = data?.cgroup_max_gb ?? 0;
  const memPct = cgroupMax > 0 ? (cgroupMem / cgroupMax) * 100 : 0;
  const kills = data?.kill_count ?? 0;
  const rssLimit = data?.rss_limit_gb ?? 0;
  const uptime = data?.uptime_seconds ?? 0;
  const qgHeld = data?.qg_tokens_held ?? -1;
  const qgMax = data?.qg_tokens_max ?? -1;
  const qgKnown = qgHeld >= 0 && qgMax > 0;

  const label = active && fresh ? "watchdog" : "watchdog (stale)";
  const tone = error ? "blocked" : pressure === "high" ? "blocked" : "resources";

  return (
    <Panel title={label} tone={tone}>
      <div style={{ display: "flex", gap: 8, padding: "10px 12px", flexWrap: "wrap" }}>
        {/* Service health */}
        <Tile
          label="service"
          value={error ? "err" : active ? (fresh ? "live" : "stale") : "dead"}
          tone={error ? _RED : serviceTone(active, fresh)}
        />

        {/* Pressure level */}
        <Tile
          label="pressure"
          value={pressure}
          tone={pressureTone(pressure)}
          title={
            pressure === "high"
              ? `RSS limit lowered to ${rssLimit} GB per process`
              : `RSS limit: ${rssLimit} GB per process`
          }
        />

        {/* Cgroup memory */}
        <Tile
          label="cgroup"
          value={`${memPct.toFixed(0)}%`}
          sub={`${cgroupMem} / ${cgroupMax} GB`}
          tone={memPct >= 80 ? _RED : memPct >= 60 ? _AMBER : _GREEN}
        />

        {/* Kill count */}
        <Tile
          label="kills"
          value={String(kills)}
          sub={data?.last_kill_iso ? `last: ${data.last_kill_iso.slice(11, 19)}` : "none yet"}
          tone={killCountTone(kills)}
        />

        {/* Uptime */}
        <Tile label="uptime" value={formatUptime(uptime)} tone={_MUTE} />

        {/* QG governor */}
        {qgKnown ? (
          <Tile
            label="QG tokens"
            value={`${qgHeld}/${qgMax}`}
            sub="held"
            tone={qgHeld >= qgMax ? _AMBER : _MUTE}
            title="QG host governor token concurrency — QG runs currently in-flight vs max allowed"
          />
        ) : (
          <Tile label="QG tokens" value="—" tone={_MUTE} title="QG host governor not reachable" />
        )}
      </div>
    </Panel>
  );
}

function Tile({
  label,
  value,
  sub,
  tone,
  title,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: string;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        background: "var(--bg-1)",
        border: "1px solid var(--bg-2)",
        borderRadius: 6,
        padding: "8px 14px",
        minWidth: 80,
        flex: "1 1 0",
      }}
    >
      <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: tone }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: _MUTE, marginTop: 2 }}>{label}</div>
      {sub && (
        <div className="mono" style={{ fontSize: 10.5, color: _MUTE, marginTop: 1 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
